import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { toast } from 'sonner'
import { useForm } from '@tanstack/react-form'
import { useState } from 'react'
import { capture, PostHogEvent } from '~/client/common/posthog'

export const Route = createFileRoute('/forgot-password')({
  component: RouteComponent,
})

type ForgotPasswordValues = {
  email: string
}

function RouteComponent() {
  const [emailSent, setEmailSent] = useState(false)

  const defaultValues: ForgotPasswordValues = {
    email: '',
  }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      capture(PostHogEvent.PASSWORD_RESET_REQUESTED)
      try {
        const response = await fetch('/api/sendPasswordResetEmail', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: value.email }),
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to send reset email')
        }

        setEmailSent(true)
        toast.success('Password reset email sent! Check your inbox.', {
          position: 'bottom-right',
          richColors: true,
        })
      } catch (err: any) {
        const message = err?.message || 'Failed to send reset email'
        toast.error(message, {
          position: 'bottom-right',
          richColors: true,
        })
      }
    },
  })

  if (emailSent) {
    return (
      <div className="p-6 md:p-10 flex flex-col items-center justify-center min-h-screen">
        <div className="w-full max-w-md">
          <div className="flex flex-col gap-6 text-center">
            <h1 className="text-2xl font-bold text-[#18003D]">Check Your Email</h1>
            <p className="text-gray-600">We've sent a password reset link to your email address. Click the link in the email to reset your password.</p>
            <p className="text-sm text-gray-500">Didn't receive the email? Check your spam folder or try again.</p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => setEmailSent(false)} variant="outline">
                Try Again
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="w-full">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 md:p-10 flex flex-col items-center justify-center min-h-screen">
      <div className="w-full max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-[#18003D]">Forgot Password</h1>
            <p className="text-sm text-gray-600">Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          <div className="grid gap-3">
            <Label htmlFor="email">Email</Label>
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return 'Email is required'
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Please enter a valid email address'
                  }
                  return undefined
                },
              }}
              children={(field) => (
                <>
                  <Input
                    id={field.name}
                    type="email"
                    placeholder="you@example.com"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                  />
                  {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                </>
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]) => (
              <div className="flex flex-col gap-3">
                <Button type="submit" className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    Back to Login
                  </Button>
                </Link>
              </div>
            )}
          />
        </form>
      </div>
    </div>
  )
}
